import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Check } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

export type MemberPlan = "free" | "weekly" | "monthly" | "quarterly";

interface PlanComparisonProps {
  currentPlan?: MemberPlan;
  showWeeklyOffer?: boolean;
  onSelectWeekly?: () => void;
  onSelectMonthly?: () => void;
  onSelectQuarterly?: () => void;
  weeklyBusy?: boolean;
  monthlyBusy?: boolean;
  quarterlyBusy?: boolean;
}

type DisplayPlan = {
  key: MemberPlan;
  /** Short label inside the top pill */
  pillLabel: string;
  price: string;
  originalPrice?: string;
  savings?: string;
  priceHint?: string;
  summary: string;
  features: string[];
};

const tierRank: Record<MemberPlan, number> = {
  free: 0,
  weekly: 1,
  monthly: 2,
  quarterly: 3,
};

function getSelectHandler(
  key: MemberPlan,
  props: PlanComparisonProps
): (() => void) | undefined {
  if (key === "weekly") return props.onSelectWeekly;
  if (key === "monthly") return props.onSelectMonthly;
  if (key === "quarterly") return props.onSelectQuarterly;
  return undefined;
}

function getBusy(key: MemberPlan, props: PlanComparisonProps): boolean {
  if (key === "weekly") return props.weeklyBusy ?? false;
  if (key === "monthly") return props.monthlyBusy ?? false;
  if (key === "quarterly") return props.quarterlyBusy ?? false;
  return false;
}

function ctaLabelForSelection(
  current: MemberPlan,
  selected: MemberPlan
): string {
  if (selected === current) {
    if (selected === "free") return "Included on free tier";
    if (selected === "weekly") return "Renew trial week";
    if (selected === "monthly") return "Renew 1 month";
    if (selected === "quarterly") return "Renew 3 months";
  }
  if (selected === "free") return "Included on free tier";

  if (selected === "weekly") {
    return current === "free"
      ? "Upgrade with one-week trial"
      : "Add one-week trial";
  }
  if (selected === "monthly") {
    if (current === "free") return "Upgrade to monthly";
    if (current === "weekly") return "Switch to monthly";
    return "Switch to monthly";
  }
  if (selected === "quarterly") {
    if (current === "free" || current === "weekly") return "Upgrade to 3 months";
    return "Switch to 3 months";
  }
  return "Continue";
}

export function PlanComparison({
  currentPlan = "free",
  showWeeklyOffer = false,
  onSelectWeekly,
  onSelectMonthly,
  onSelectQuarterly,
  weeklyBusy = false,
  monthlyBusy = false,
  quarterlyBusy = false,
}: PlanComparisonProps) {
  const props = {
    showWeeklyOffer,
    onSelectWeekly,
    onSelectMonthly,
    onSelectQuarterly,
    weeklyBusy,
    monthlyBusy,
    quarterlyBusy,
  };

  const includeWeekly = showWeeklyOffer || currentPlan === "weekly";

  const plans: DisplayPlan[] = useMemo(
    () => [
      {
        key: "free",
        pillLabel: "Free",
        price: "Free",
        summary:
          "Core coaching at no cost. Upgrade when you want unlimited practice and premium tools.",
        features: [
          "Organize your job hunt",
          "2 three-question interviews per week",
          "1 thirty-second pitch per day",
          "No targeted pitch practice",
        ],
      },
      ...(includeWeekly
        ? [
            {
              key: "weekly" as const,
              pillLabel: "Trial week",
              price: "$4.99",
              originalPrice: "$7.99",
              savings: "$3 off this purchase",
              summary:
                "Full access for seven days. One-time purchase per eligible account for that one interview or to try us out.",
              features: [
                "Everything unlocked during the trial window",
                "Everyone gets one",
              ],
            },
          ]
        : []),
      {
        key: "monthly",
        pillLabel: "1 month",
        price: "$24.99",
        summary:
          "Unlimited interviews, pitches, and premium coaching for a full month.",
        features: [
          "Unlimited personalized interview practice",
          "Unlimited pitch and networking practice",
          "Salary negotiation role playing",
          "Resume analysis and targeted coaching",
        ],
      },
      {
        key: "quarterly",
        pillLabel: "3 months",
        price: "$49.99",
        originalPrice: "$74.99",
        savings: "One month free",
        summary:
          "Unlimited premium for 3 months at an unbeatable price.",
        features: [
          "Unlimited personalized interview practice",
          "Unlimited pitch and networking practice",
          "Salary negotiation role playing",
          "Resume analysis and targeted coaching",
        ],
      },
    ],
    [includeWeekly]
  );

  const [selected, setSelected] = useState<MemberPlan>(currentPlan);

  useEffect(() => {
    setSelected(currentPlan);
  }, [currentPlan]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.key === selected) ?? plans[0],
    [plans, selected]
  );

  const isSelectedCurrent = selected === currentPlan;
  const onSelect = getSelectHandler(selected, props);
  const busy = getBusy(selected, props);

  const isDowngradeOrIneligible =
    selected !== currentPlan &&
    (selected === "free" && currentPlan !== "free"
      ? true
      : tierRank[selected] < tierRank[currentPlan]);

  const ctaDisabled =
    selected === "free" ||
    !onSelect ||
    isDowngradeOrIneligible ||
    busy;

  const ctaLabel = ctaLabelForSelection(currentPlan, selected);

  const gridCols =
    plans.length >= 4
      ? "grid-cols-2 sm:grid-cols-2 lg:grid-cols-4"
      : "grid-cols-1 sm:grid-cols-3";

  return (
    <div className="rounded-[2rem] border border-slate-200 bg-[#f4f8fc] p-6 shadow-sm md:p-8">
      <div className="mx-auto max-w-3xl text-center">
        <p className="mb-2 text-sm font-semibold uppercase tracking-[0.28em] text-sky-700">
          Subscription
        </p>
        <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">
          Choose your plan
        </h2>
        <p className="mt-3 text-base text-slate-600 md:text-lg">
          Tap a tier to see what&apos;s included. Upgrade or switch with a
          one-time checkout—nothing auto-renews unless you choose to buy again. Any issues? <a href="/contact" className="text-sky-600 hover:text-sky-500">Let us know</a> and we'll take care of you.
        </p>
      </div>

      {/* Plan pills */}
      <div className={cn("mx-auto mt-10 grid max-w-4xl gap-3", gridCols)}>
        {plans.map((plan) => {
          const isCurrent = currentPlan === plan.key;
          const isSelected = selected === plan.key;
          return (
            <button
              key={plan.key}
              type="button"
              onClick={() => setSelected(plan.key)}
              className={cn(
                "flex flex-col rounded-2xl border-2 bg-white p-4 text-left shadow-sm transition-all",
                "hover:border-sky-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2",
                isSelected
                  ? "border-sky-500 ring-2 ring-sky-400 ring-offset-2"
                  : "border-slate-200",
                isCurrent && !isSelected && "border-emerald-200 bg-emerald-50/40"
              )}
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                {plan.pillLabel}
              </span>
              <span className="mt-1 text-xl font-bold tabular-nums text-slate-900">
                {plan.price}
              </span>
              {isCurrent && (
                <span className="mt-2 inline-flex w-fit rounded-full bg-slate-900 px-2.5 py-0.5 text-[11px] font-semibold text-white">
                  Current
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected plan detail */}
      <Card className="mx-auto mt-8 max-w-2xl overflow-hidden rounded-2xl border-slate-200 shadow-md">
        <CardContent className="p-6 sm:p-8">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            <h3 className="text-xl font-bold text-slate-900">
              {selectedPlan.pillLabel}
            </h3>
            {isSelectedCurrent && (
              <span className="text-sm font-medium text-emerald-700">
                This is your active plan. Renew now to extend it.
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-3xl font-bold tabular-nums text-slate-900 sm:text-4xl">
              {selectedPlan.price}
            </span>
            {selectedPlan.originalPrice && (
              <>
                <span className="text-base text-slate-400 line-through">
                  {selectedPlan.originalPrice}
                </span>
                {selectedPlan.savings && (
                  <span className="text-sm font-semibold text-emerald-600">
                    {selectedPlan.savings}
                  </span>
                )}
              </>
            )}
          </div>
          {selectedPlan.priceHint && (
            <p className="mt-2 text-sm text-slate-500">{selectedPlan.priceHint}</p>
          )}

          <p className="mt-5 text-base leading-relaxed text-slate-600">
            {selectedPlan.summary}
          </p>

          <ul className="mt-6 space-y-3 text-sm text-slate-700">
            {selectedPlan.features.map((line) => (
              <li key={line} className="flex gap-3">
                <Check
                  className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500"
                  aria-hidden
                />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          {isDowngradeOrIneligible && selected !== currentPlan && (
            <p className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {selected === "free"
                ? "Moving back to the free tier isn’t available in checkout. Contact support if you need help."
                : "Switching to this shorter or lower tier isn’t available here. Contact support if you need to adjust your plan."}
            </p>
          )}

          <div className="mt-8">
            <Button
              className={cn(
                "flex h-auto min-h-11 w-full items-center justify-center gap-2 whitespace-normal text-center text-base font-semibold leading-snug rounded-full px-4 py-6",
                selected === "free"
                  ? "border border-slate-400 bg-slate-100 text-slate-900 shadow-sm disabled:cursor-default disabled:opacity-100"
                  : "bg-sky-600 text-white hover:bg-sky-500 disabled:opacity-60"
              )}
              disabled={ctaDisabled}
              onClick={onSelect}
              variant="default"
            >
              {busy ? "Starting checkout…" : ctaLabel}
              {!ctaDisabled && !busy && (
                <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default PlanComparison;
